import { useEffect } from 'react'
import { Outlet, Navigate, useLocation } from 'react-router-dom'
import { Sidebar } from './sidebar'
import { Header } from './header'
import { ImpersonationBanner } from './impersonation-banner'
import { useAuthStore } from '@/shared/stores/auth.store'
import { useSidebarStore } from '@/shared/stores/sidebar-store'
import { useContextStore } from '@/shared/stores/context.store'
import { AnnouncementDetailModalHost } from '@/features/announcements/components/announcement-detail-modal'
import { AskAiWidget } from '@/features/ask-ai/components/ask-ai-widget'
import { WalkthroughHost } from '@/features/walkthrough/components/walkthrough-host'
import { navigation } from '@/app/navigation'

export function AppShell() {
  const { isAuthenticated, isLoading, isBootstrapped } = useAuthStore()
  const { collapsed } = useSidebarStore()
  const location = useLocation()
  const resetFilters = useContextStore((s) => s.resetFilters)

  // Reset context filters on route change — each page starts with clean filter state
  useEffect(() => {
    resetFilters()
  }, [location.pathname, resetFilters])

  // While auth is still bootstrapping we don't yet know whether to show the app
  // chrome or send the user to login. Render a minimal full-page loader instead
  // of flashing sidebar/header that may immediately unmount on a redirect.
  if (!isBootstrapped || isLoading) {
    return <FullPageLoader />
  }

  // Bootstrap settled, user is not authenticated → bounce to login, preserving
  // the originally requested path so we can return after a successful sign-in.
  if (!isAuthenticated) {
    const next = location.pathname + location.search + location.hash
    const isAtRoot = location.pathname === '/' && !location.search && !location.hash
    const to = isAtRoot ? '/auth/login' : `/auth/login?next=${encodeURIComponent(next)}`
    return <Navigate to={to} replace />
  }

  return (
    <>
      <ImpersonationBanner />
      <Sidebar />
      <Header />
      <main className={`main-wrapper${collapsed ? ' sidebar-collapsed' : ''}`}>
        <div className="main-content" data-tour="shell.main">
          <Outlet />
        </div>
      </main>
      <AnnouncementDetailModalHost />
      <AskAiWidget />
      <WalkthroughHost navGroups={navigation} />
    </>
  )
}

function FullPageLoader() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--bg)',
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          border: '3px solid var(--primary-200)',
          borderTopColor: 'var(--primary)',
          animation: 'spin 0.8s linear infinite',
        }}
      />
    </div>
  )
}
